import { get, IRequestOptions, post } from '@apis/api';
import { MolocoAdProductRespose } from '@models/moloco';
import {
  PromotionAggsResponse,
  PromotionFilterNamesResult,
  PromotionProductResponse,
} from '@models/promotion';
import { CommonResponse } from '@wconcept/wconcept-common/models/common-response';
import { ICategoryProdct } from '@wconcept/wconcept-common/models/event/custom';
import { LpFilterRequestAggs } from '@wconcept/wconcept-common/models/renewal/common';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import {
  PromotionDetailResponse,
  PromotionTopBuyerDetailResponse,
  PromotionTargetDetailResponse,
  PromotionCategory,
} from '@wconcept/wconcept-common/models/renewal/promotion';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const eventBaseURL = process.env.NEXT_PUBLIC_EVENT_API_BASE_URL;

const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'EXTENSION-API-KEY': process.env.NEXT_PUBLIC_EXTENSION_API_KEY || '',
  },
};

const eventOptions = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'EVENT-API-KEY': process.env.NEXT_PUBLIC_EVENT_API_KEY || '',
  },
};

export function getPromotion(
  promotionId: string,
  optionParams: IRequestOptions = {}
) {
  return get<CommonResponse<PromotionDetailResponse>>({
    url: `/extension/api/promotion/v1/${promotionId}`,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
  });
}

export function getPromotionAdmin(
  promotionId: string,
  optionParams: IRequestOptions = {}
) {
  return get<CommonResponse<PromotionDetailResponse>>({
    url: `/extension/api/promotion/v1/${promotionId}/admin`,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
  });
}

export function requestGetPromotionTopBuyer(
  promotionId: string,
  optionParams: IRequestOptions = {}
) {
  return get<CommonResponse<PromotionTopBuyerDetailResponse>>({
    url: `/extension/api/promotion/v1/${promotionId}/detail/TOP_BUYER`,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
  });
}

/**
 *
 */
export const requestGetPromotionProductsMoloco = async (payload: {
  id: string;
  pageId: string;
  sessionId: string;
  userId: string;
  categoryFilter?: string[];
}) => {
  const { data } = await post<MolocoAdProductRespose>({
    url: `/extension/api/promotion/v1/products/moloco`,
    options,
    baseUrl: baseURL,
    data: payload,
  });
  return data;
};

/**
 *
 * @summary 실시간 랭킹 상품 목록
 * @param {string} categoryKey
 */
export const requestGetCategoryRankProducts = async (categoryKey: string) => {
  const { data } = await get<CommonResponse<ICategoryProdct[]>>({
    url: `/event/api/v1/recommend/wweek/${categoryKey}`,
    baseUrl: eventBaseURL,
    options: eventOptions,
  });
  return data;
};

/**
 * @summary 응모 이벤트 참여 이력 조회
 */
export const requestGetPromotionEntryApplied = async (
  promotionId: number,
  moduleId: number
) => {
  const { data } = await get<CommonResponse<{ applied: boolean }>>({
    url: `/extension/api/promotion/v1/${promotionId}/entry/${moduleId}/applied`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

/**
 * @summary 응모 이벤트 참여 적재
 */
export const requestPromotionEntry = async (
  promotionId: number,
  moduleId: number
) => {
  const res = await post<
    CommonResponse<{ promotionId: number; moduleId: number }>
  >({
    url: `/extension/api/promotion/v1/${promotionId}/entry/${moduleId}`,
    options,
    baseUrl: baseURL,
    data: { promotionId, moduleId },
  });
  return res;
};

// 01: 판매중, 02: 일시품절, 04: 품절, 05: 단종
// /extension/api/product/v1/list
export const requestGetPromotionProducts = async (params: {
  itemCdList: string[];
  itemStatusCdList?: Array<'01' | '02' | '04' | '05'>;
  productType?: 'GENERAL' | 'SECRET';
}) => {
  const { productType, ...rest } = params;
  const data = productType !== undefined ? { ...rest, productType } : rest;

  const { data: responseData } = await post<
    CommonResponse<{
      productList: Product[];
    }>
  >({
    url: `/extension/api/product/v1/list`,
    data,
    options,
    baseUrl: baseURL,
  });
  return responseData;
};
export default getPromotion;

// https://dev-gw-front.wconcept.co.kr/extension/api/promotion/v1/12/detail/SPECIAL_PRICE
export const requestGetPromotionSpecialPrice = async (
  promotionId: string,
  optionParams: IRequestOptions = {}
) => {
  const { data } = await get<CommonResponse<PromotionTargetDetailResponse>>({
    url: `/extension/api/promotion/v1/${promotionId}/detail/SPECIAL_PRICE`,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
  });
  return data;
};

// https://dev-gw-front.wconcept.co.kr/extension/api/promotion/v1/12/detail/RANKING
export const requestGetPromotionRanking = async (promotionId: string) => {
  const { data } = await get<CommonResponse<PromotionTargetDetailResponse>>({
    url: `/extension/api/promotion/v1/${promotionId}/detail/RANKING`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

// /extension/api/promotion/v1/server-time
export const requestGetPromotionServerTime = async (
  optionParams: IRequestOptions = {}
) => {
  const res = await get<string>({
    url: `/extension/api/promotion/v1/server-time`,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
  });
  return res;
};

// /extension/api/promotion/v1/products/recommend
export const requestGetPromotionProductsRecommend = async (payload: {
  promotionId: number;
  contentId: number;
  moduleId: number;
  recommendItems: string[];
}) => {
  const { data } = await post<CommonResponse<{ items: Product[] }>>({
    url: `/extension/api/promotion/v1/products/recommend`,
    options,
    baseUrl: baseURL,
    data: payload,
  });
  return data;
};

interface RequestGetPromotionProductsLpAggsParams extends LpFilterRequestAggs {
  aggs: string[];
  status?: string[];
  mcd: string;
  ccds: string[];
  pcds: string[];
  lcds: string[];
  mainCategories: PromotionCategory[];
}

// /extension/api/promotion/v1/products/lp/aggs
export const requestGetPromotionProductsLpAggs = async (
  payload: RequestGetPromotionProductsLpAggsParams
) => {
  const { data } = await post<PromotionAggsResponse>({
    url: `/extension/api/promotion/v1/products/lp/aggs`,
    options,
    baseUrl: baseURL,
    data: payload,
  });
  return data;
};

// /extension/api/promotion/v1/products/lp/init
export const requestGetPromotionProductsLpInit = async ({
  mcd,
  ccds,
  pcds,
  lcds,
  mainCategories,
  status,
  optionParams,
  promotionId,
  contentId,
  moduleId,
}: {
  mcd: string;
  ccds: string[];
  pcds: string[];
  lcds: string[];
  status: string[];
  mainCategories: PromotionCategory[];
  optionParams?: IRequestOptions;
  promotionId?: number;
  contentId?: number;
  moduleId?: number;
}) => {
  const { data } = await post<CommonResponse<PromotionFilterNamesResult>>({
    url: `/extension/api/promotion/v1/products/lp/init`,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
    data: {
      mcd,
      ccds,
      pcds,
      status,
      lcds,
      mainCategories,
      ...(promotionId != null && { promotionId }),
      ...(contentId != null && { contentId }),
      ...(moduleId != null && { moduleId }),
    },
  });
  return data;
};

/**
 * LP 영역 상품 목록 조회
 */
export const requestGetPromotionProductsLp = async ({
  promotionId,
  pageParam,
  mainCategories,
  status,
  mcd,
  ccds,
  pcds,
  lcds,
  sort,
  size,
  contentId,
  moduleId,
  tabId,
}: {
  promotionId: string;
  pageParam: number;
  mainCategories: PromotionCategory[];
  status?: string[];
  mcd: string;
  ccds: string[];
  pcds: string[];
  lcds: string[];
  sort: string;
  size: number;
  contentId?: number;
  moduleId?: number;
  tabId?: number | null;
}) => {
  const { data } = await post<PromotionProductResponse>({
    url: `/extension/api/promotion/v1/products/lp`,
    data: {
      sort,
      size,
      page: pageParam,
      status,
      mcd,
      ccds,
      pcds,
      lcds,
      mainCategories,
      ...(promotionId != null && { promotionId }),
      ...(contentId != null && { contentId }),
      ...(moduleId != null && { moduleId }),
      ...(tabId !== undefined && { tabId }),
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};
export interface ICommonNavigationTab {
  tabId: number;
  navigationId: number;
  tabSort: number;
  startDate: string;
  endDate: string;
  tabTitle1: string;
  tabTitle2: string;
  landingType: 'MEGA_PROMOTION' | 'BRAND_PROMOTION' | 'EVENT';
  landingId: string;
  landingLabel: string;
  deleted: boolean;
}

export interface ICommonNavigation {
  navigationId: number;
  navigationTitle: string;
  displayStartDate: string;
  displayEndDate: string;
  displayStatus: string;
  onBgColor: string;
  offBgColor: string;
  onTextColor: string;
  offTextColor: string;
  tabs: ICommonNavigationTab[];
}
/**
 * 공통 내비게이션 조회
 * @param navigationId - 내비게이션 ID
 * @param eventId - 이벤트 ID
 * @param displayDate - 선택적 표시 날짜
 * @returns CommonResponse<ICommonNavigation>
 */
export const requestGetCommonNavigation = async (
  navigationId: number,
  landingId: number,
  displayDate?: string,
  landingType?: string
) => {
  const params = new URLSearchParams({ landingId: landingId.toString() });
  if (landingType) {
    params.append('landingType', landingType);
  }
  if (displayDate) {
    params.append('displayDate', displayDate);
  }

  const { data } = await get<CommonResponse<ICommonNavigation>>({
    url: `/event/api/v1/navigation/${navigationId}?${params.toString()}`,
    baseUrl: eventBaseURL,
    options: eventOptions,
  });
  return data;
};
